import type { ComponentProps } from 'react'

import { t } from 'i18next'
import { useNavigate } from 'react-router'

import { AvatarPessoa } from '@/components/avatar-pessoa'
import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, Skeleton } from '@/components/ui'
import { IncluirCpfLeadRepresadoSchema } from '@/features/leads-represados/brasil/domain/schemas/incluir-cpf-lead-represado.schema'
import { useSuspenseLeadRepresadoPorId } from '@/features/leads-represados/brasil/hooks/use-suspense-lead-represado-por-id'
import { useAtendimento } from '@/hooks/use-atendimento'
import { useMutationAtualizarClientes } from '@/hooks/use-atualizar-clientes'
import { DestructiveActionGuard } from '@/shared/destructive-action-guard'
import { DialogIncluirCpfCliente } from '@/shared/dialog-incluir-cpf-cliente'
import { StringUtils } from '@/utils/string.utils'

const ButtonIniciarAtendimento = (props: ComponentProps<typeof Button>) => {
  return (
    <Button className="h-12 w-full" variant="outline" {...props}>
      <Icon name="square-play" size={16} />
      {t('leads-represados:pages.detalhes.botoes.iniciar_atendimento')}
    </Button>
  )
}

export const DetalhesLeadRepresadoContent = ({ leadId }: { leadId: string }) => {
  const { data } = useSuspenseLeadRepresadoPorId(leadId)
  const { handleIniciarAtendimento, isEmAtendimento } = useAtendimento()
  const navigate = useNavigate()
  const handleNavigateToSimulacoes = async () =>
    handleIniciarAtendimento(leadId, () => navigate('/historico/simulacoes'))
  const { mutateAsync: onAtualizarCliente } = useMutationAtualizarClientes()

  const navigateToEnvioDocumentos = () => {
    navigate(`/leads-represados/${leadId}/enviar-documentos`, { state: { pessoa: data } })
  }
  const handleAbrirDialogDocumentos = async () => {
    if (data.cpf?.includes('não informado')) {
      const result = await DialogIncluirCpfCliente.show({
        titulo: t('leads-represados:pages.detalhes.incluir_cpf.titulo'),
        labels: {
          cpf: t('leads-represados:pages.detalhes.incluir_cpf.labels.cpf'),
          placeholder: t('leads-represados:pages.detalhes.incluir_cpf.labels.placeholder'),
          botaoConfirmar: t('leads-represados:pages.detalhes.incluir_cpf.labels.botao_confirmar'),
          botaoCancelar: t('leads-represados:pages.detalhes.incluir_cpf.labels.botao_cancelar'),
        },
        nome: data.nome,
        telefone: data.telefone ?? undefined,
        schema: IncluirCpfLeadRepresadoSchema,
        containerClass: 'sm:max-w-135',
      })
      if (result.value === 'confirm' && result.data?.cpf) {
        await onAtualizarCliente({ id: leadId, cpf: result.data.cpf }, { onSuccess: navigateToEnvioDocumentos })
      }
      return
    }
    navigateToEnvioDocumentos()
  }
  return (
    <div className="border border-neutral-200 p-4">
      <div className="mb-8 flex items-center gap-4 py-2">
        <AvatarPessoa className="h-9 w-9 text-sm" nome={data.nome} />
        <h2>{data.nome}</h2>
      </div>
      <ul className="text-md grid gap-1">
        <li>
          {StringUtils.singularPlural(
            data.empreendimentos.length,
            t('leads-represados:pages.detalhes.informacoes.interesse'),
            t('leads-represados:pages.detalhes.informacoes.interesses'),
          )}
          : {data.empreendimentos.join(', ')}
        </li>
        <Render.If condition={data.telefone}>{() => <li className="text-neutral-600">{data.telefone}</li>}</Render.If>
        <Render.If condition={data.cpf}>{() => <li className="text-neutral-600">{data.cpf}</li>}</Render.If>
        <li className={`${data.quantidadeDocumentosEnviados ? 'text-green-800' : 'text-red-600'}`}>
          {data.quantidadeDocumentosEnviados ? (
            <span>
              {`${data.quantidadeDocumentosEnviados} `}
              {StringUtils.singularPlural(
                data.quantidadeDocumentosEnviados,
                t('common:documentos.anexado'),
                t('common:documentos.anexados'),
              )}
            </span>
          ) : (
            <span>{t('common:documentos.nenhum')}</span>
          )}
        </li>
      </ul>
      <div className="mt-8 flex flex-col gap-4 border-t p-4 md:flex-row md:gap-16 md:px-10 md:pt-7.5 md:pb-9">
        <Render.If
          condition={isEmAtendimento}
          fallback={<ButtonIniciarAtendimento onClick={handleNavigateToSimulacoes} />}
        >
          {() => {
            return (
              <DestructiveActionGuard
                title={t('leads-represados:pages.detalhes.iniciar_atendimento.titulo')}
                description={t('leads-represados:pages.detalhes.iniciar_atendimento.descricao')}
                actions={[
                  {
                    title: t('leads-represados:pages.detalhes.iniciar_atendimento.btn_cancel'),
                    value: 'cancel',
                    variant: 'outline',
                  },
                  {
                    title: t('leads-represados:pages.detalhes.iniciar_atendimento.btn_confirm'),
                    value: 'confirm',
                    variant: 'default',
                  },
                ]}
              >
                <ButtonIniciarAtendimento onClick={handleNavigateToSimulacoes} />
              </DestructiveActionGuard>
            )
          }}
        </Render.If>
        <Button className="h-12 w-full" onClick={handleAbrirDialogDocumentos}>
          <Icon name="files" />
          {t('leads-represados:pages.detalhes.botoes.documentos')}
        </Button>
      </div>
    </div>
  )
}

export const DetalhesLeadRepresadoContentSkeleton = () => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <Skeleton className="mb-4 h-6 w-48" />
          <div className="space-y-3">
            <div>
              <Skeleton className="mb-1 h-4 w-16" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div>
              <Skeleton className="mb-1 h-4 w-12" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div>
              <Skeleton className="mb-1 h-4 w-20" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
        <div>
          <Skeleton className="mb-4 h-6 w-56" />
          <div className="space-y-3">
            <div>
              <Skeleton className="mb-1 h-4 w-28" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div>
              <Skeleton className="mb-1 h-4 w-14" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div>
              <Skeleton className="mb-1 h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
